import Phaser from "phaser";
import { TOWERS, towerStatsForInstance } from "../config/towers";
import { ENEMIES } from "../config/enemies";
import { events } from "../core/events";
import { AudioManager } from "../systems/audio";
import { loadSave, saveDiagnostics } from "../systems/save";
import { currentPlayerIsAdministrator } from "../systems/admin-access";
import { getPlatformSyncDiagnostic } from "../systems/platform-integration";
import { progressionDiagnostics } from "../systems/progression";
import { completeQaAudit } from "../systems/qa-audit";
import type { DefenseGameState } from "../core/game-state";
import { addButton, COLORS } from "./shared";

export class UIScene extends Phaser.Scene {
  private state!: DefenseGameState;
  private hud!: Phaser.GameObjects.Text;
  private message!: Phaser.GameObjects.Text;
  private buildBar!: Phaser.GameObjects.Container;
  private panel!: Phaser.GameObjects.Container;
  private pausePanel!: Phaser.GameObjects.Container;
  private draftPanel!: Phaser.GameObjects.Container;
  private announcement!: Phaser.GameObjects.Container;
  private debugPanel!: Phaser.GameObjects.Container;
  private administratorDebug = false;
  private debugPage = 0;
  private audio!: AudioManager;
  private nextInfo: Phaser.GameObjects.Text | null = null;
  private waveButtonLabel: Phaser.GameObjects.Text | null = null;
  private unsubscribers: (() => void)[] = [];
  private shortcutHandlers: { event: string; handler: () => void }[] = [];
  constructor() {
    super("UI");
  }
  create(): void {
    this.events.once(Phaser.Scenes.Events.SHUTDOWN, this.shutdown, this);
    this.state = this.registry.get("defense-state") as DefenseGameState;
    this.audio = new AudioManager(loadSave().settings);
    this.audio.bindUnlock(this);
    this.hud = this.add
      .text(12, 10, "", {
        fontFamily: "Trebuchet MS, sans-serif",
        fontSize: "16px",
        fontStyle: "bold",
        color: "#fff3cf",
        backgroundColor: "#28221de8",
        padding: { x: 10, y: 7 },
      })
      .setDepth(100)
      .setStroke("#000000", 1);
    this.message = this.add
      .text(this.scale.width / 2, 48, "", {
        fontFamily: "Trebuchet MS, sans-serif",
        fontSize: "14px",
        color: "#ffd778",
        backgroundColor: "#351812dd",
        padding: { x: 9, y: 5 },
      })
      .setOrigin(0.5)
      .setDepth(102)
      .setScale(loadSave().settings.uiScale)
      .setVisible(false);
    this.buildBar = this.add.container(0, 0).setDepth(100);
    this.panel = this.add.container(0, 0).setDepth(103);
    this.pausePanel = this.add.container(0, 0).setDepth(110);
    this.draftPanel = this.add.container(0, 0).setDepth(120);
    this.announcement = this.add.container(0, 0).setDepth(130).setVisible(false);
    this.debugPanel = this.add.container(0, 0).setDepth(145);
    // MainMenu verifies the account before gameplay and stores the result in the
    // shared registry. Apply that result synchronously so the first HUD build
    // already contains the in-game Admin Debug button.
    this.administratorDebug = this.registry.get("defense-administrator") === true;
    this.rebuild();
    this.unsubscribers.push(
      events.on("state:changed", () => this.refresh()),
      events.on("tower:selected", () => this.rebuildPanel()),
      events.on("tower:upgraded", () => this.rebuildPanel()),
      events.on("tower:sold", () => this.rebuildPanel()),
      events.on("placement:feedback", ({ message }) => this.flash(message)),
      events.on("draft:offered", () => this.rebuildDraftPanel()),
      events.on("draft:selected", () => this.rebuildDraftPanel()),
      events.on("wave:started", ({ wave }) => this.showWaveAnnouncement(wave)),
      events.on("wave:completed", ({ wave }) => this.showWaveComplete(wave)),
    );
    this.scale.on("resize", this.rebuild, this);
    ["ONE", "TWO", "THREE", "FOUR"].forEach((key, index) => {
      const event = `keydown-${key}`,
        handler = () => {
          const towerId = this.state.loadedTowerIds[index];
          const tower = towerId ? TOWERS[towerId] : undefined;
          if (tower) {
            this.state.selectBuild(tower.id);
            this.rebuild();
          }
        };
      this.shortcutHandlers.push({ event, handler });
      this.input.keyboard?.on(event, handler);
    });
    const cancelPlacement = () => {
      if (this.state.selectedBuild === null) return;
      this.state.selectBuild(null);
      this.rebuild();
    };
    this.shortcutHandlers.push({ event: "keydown-ESC", handler: cancelPlacement });
    this.input.keyboard?.on("keydown-ESC", cancelPlacement);
    const openDebug = () => {
      if (this.administratorDebug) this.rebuildDebugPanel();
    };
    this.shortcutHandlers.push({ event: "keydown-F8", handler: openDebug });
    this.input.keyboard?.on("keydown-F8", openDebug);
    void this.installAdministratorDebug();
  }
  shutdown(): void {
    for (const unsubscribe of this.unsubscribers) unsubscribe();
    this.unsubscribers = [];
    this.scale.off("resize", this.rebuild, this);
    for (const { event, handler } of this.shortcutHandlers)
      this.input.keyboard?.off(event, handler);
    this.shortcutHandlers = [];
    this.tweens.killAll();
    this.time.removeAllEvents();
  }
  private refresh(): void {
    const mobile = this.scale.width < 700;
    const uiScale = loadSave().settings.uiScale;
    this.hud
      .setFontSize(Math.round((mobile ? 12 : 16) * uiScale))
      .setColor(this.state.lives <= 5 ? "#ffb0a3" : "#fff3cf")
      .setText(
        mobile
          ? `♜ ${this.state.lives}  ◆ ${this.state.economy.balance}  WAVE ${this.state.waves.currentWaveNumber}/${this.state.waves.totalWaves}\nRUNNERS ${this.state.waves.remainingEnemies}  SCORE ${this.state.score}`
          : `♜ ${this.state.lives}   ◆ ${this.state.economy.balance}   WAVE ${this.state.waves.currentWaveNumber}/${this.state.waves.totalWaves}   RUNNERS ${this.state.waves.remainingEnemies}   SCORE ${this.state.score}`,
      );
    const nextWave = this.state.waves.nextWave;
    if (this.nextInfo?.active && nextWave)
      this.nextInfo.setText(
        this.state.waves.running
          ? `NEXT: ${nextWave.name}`
          : `NEXT: ${nextWave.name}\n${(this.state.preparationRemainingMs / 1000).toFixed(1)}s`,
      );
    if (this.waveButtonLabel?.active)
      this.waveButtonLabel.setText(
        this.state.waves.nextWave
          ? this.state.waves.running
            ? "Send Next Wave"
            : `Send Wave${this.state.earlyWaveReward ? ` +${this.state.earlyWaveReward}` : ""}`
          : "Final Wave",
      );
    this.rebuildPausePanel();
  }
  private rebuild(): void {
    this.buildBar.removeAll(true);
    this.nextInfo = null;
    this.waveButtonLabel = null;
    const mobile = this.scale.width < 700,
      y = this.scale.height - (mobile ? 88 : 50),
      entries = this.state.loadedTowerIds
        .map((towerId) => TOWERS[towerId])
        .filter((tower): tower is NonNullable<typeof tower> => Boolean(tower)),
      availableWidth = this.scale.width - (mobile ? 145 : 180),
      itemWidth = Math.min(164, availableWidth / entries.length);
    entries.forEach((tower, index) => {
      const x = 10 + itemWidth / 2 + index * itemWidth;
      const affordable = this.state.economy.canAfford(tower.cost);
      const background = this.add
        .rectangle(
          0,
          0,
          itemWidth - 6,
          mobile ? 67 : 46,
          this.state.selectedBuild === tower.id
            ? COLORS.gold
            : affordable
              ? COLORS.panel
              : 0x2b2927,
        )
        .setStrokeStyle(1, 0xe5c669)
        .setInteractive({ useHandCursor: true });
      const icon = this.add
        .image(mobile ? 0 : -itemWidth / 2 + 24, mobile ? -8 : 0, tower.texture)
        .setDisplaySize(mobile ? 34 : 38, mobile ? 34 : 38);
      const label = this.add
        .text(
          mobile ? 0 : -itemWidth / 2 + 47,
          mobile ? 20 : 0,
          mobile ? `◆${tower.cost}` : `${tower.displayName}\n◆ ${tower.cost}`,
          {
            fontFamily: "Trebuchet MS, sans-serif",
            fontSize: mobile ? "10px" : "13px",
            fontStyle: "bold",
            color:
              this.state.selectedBuild === tower.id
                ? "#2d2116"
                : affordable
                  ? "#fff3cf"
                  : "#9d968b",
          },
        )
        .setOrigin(mobile ? 0.5 : 0, 0.5);
      background.on("pointerdown", () => {
        this.state.selectBuild(tower.id);
        this.rebuild();
      });
      this.buildBar.add(this.add.container(x, y, [background, icon, label]));
    });
    if (mobile) {
      this.buildBar.add(
        this.add
          .text(
            this.scale.width / 2,
            500,
            "TAP A TOWER, THEN OPEN GRASS\nBuild a longer road. Keep Lion Gate reachable.",
            {
              fontFamily: "Georgia, serif",
              fontSize: "14px",
              color: "#d0c0a3",
              align: "center",
              lineSpacing: 6,
            },
          )
          .setOrigin(0.5),
      );
    }
    const wave = addButton(
      this,
      this.scale.width - 82,
      y,
      mobile ? 126 : 150,
      this.state.waves.nextWave
        ? this.state.waves.running
          ? "Send Next Wave"
          : `Send Wave${this.state.earlyWaveReward ? ` +${this.state.earlyWaveReward}` : ""}`
        : "Final Wave",
      () => this.state.startWave(),
      true,
    );
    const waveLabel = wave.getAt(1);
    this.waveButtonLabel = waveLabel instanceof Phaser.GameObjects.Text ? waveLabel : null;
    this.buildBar.add(wave);
    const nextWave = this.state.waves.nextWave;
    if (nextWave) {
      this.nextInfo = this.add
        .text(
          this.scale.width - (mobile ? 72 : 180),
          mobile ? y - 53 : y - 35,
          this.state.waves.running
            ? `NEXT: ${nextWave.name}`
            : `NEXT: ${nextWave.name}\n${(this.state.preparationRemainingMs / 1000).toFixed(1)}s`,
          {
            fontFamily: "Trebuchet MS, sans-serif",
            fontSize: mobile ? "10px" : "12px",
            color: "#dfc889",
            align: "center",
          },
        )
        .setOrigin(0.5);
      this.buildBar.add(this.nextInfo);
    }
    const speed = addButton(
      this,
      this.scale.width - 178,
      27,
      82,
      `${this.state.speed}× Speed`,
      () => {
        this.state.cycleSpeed();
        this.rebuild();
      },
    );
    const pause = addButton(
      this,
      this.scale.width - 65,
      27,
      104,
      this.state.paused ? "Resume" : "Pause",
      () => {
        this.state.togglePaused();
        this.rebuild();
      },
    );
    this.buildBar.add(speed);
    this.buildBar.add(pause);
    if (this.administratorDebug) {
      const debug = addButton(this, this.scale.width - 292, 27, 116, "Admin Debug", () =>
        this.rebuildDebugPanel(),
      );
      this.buildBar.add(debug);
    }
    this.refresh();
  }

  private async installAdministratorDebug(): Promise<void> {
    // Prefer the authorization already established on the main menu. If the UI
    // scene was launched directly (development/restart flows), verify again.
    const registryAuthorized = this.registry.get("defense-administrator") === true;
    const authorized = registryAuthorized || (await currentPlayerIsAdministrator());
    if (!authorized || !this.sys.isActive()) return;

    this.administratorDebug = true;
    this.registry.set("defense-administrator", true);
    this.rebuild();
  }

  private rebuildDebugPanel(): void {
    this.debugPanel.removeAll(true);
    const center = this.scale.width / 2;
    const middle = this.scale.height / 2;
    const width = Math.min(640, this.scale.width - 24);
    const height = Math.min(620, this.scale.height - 24);
    const shade = this.add
      .rectangle(center, middle, this.scale.width, this.scale.height, 0x000000, 0.78)
      .setInteractive();
    const panel = this.add
      .rectangle(center, middle, width, height, 0x211a16, 0.99)
      .setStrokeStyle(4, COLORS.gold);
    const heading = this.add
      .text(center, middle - height / 2 + 32, `BATTLE DEBUG — ${this.debugPage + 1}/3`, {
        fontFamily: "Georgia, serif",
        fontSize: "24px",
        fontStyle: "bold",
        color: "#fff3cf",
      })
      .setOrigin(0.5);
    const status = this.add
      .text(center, middle - height / 2 + 64, "F8 opens this panel • administrator only", {
        fontFamily: "Trebuchet MS, sans-serif",
        fontSize: "12px",
        color: "#e0c77f",
        align: "center",
        wordWrap: { width: width - 40 },
      })
      .setOrigin(0.5);
    const controls: Phaser.GameObjects.GameObject[] = [shade, panel, heading, status];
    const buttonWidth = Math.min(430, width - 40);
    const startY = middle - height / 2 + 110;
    const gap = 48;
    const addAction = (label: string, row: number, callback: () => void): void => {
      controls.push(
        addButton(this, center, startY + row * gap, buttonWidth, label, callback, true),
      );
    };

    if (this.debugPage === 0) {
      addAction(`Infinite Money: ${this.state.debugInfiniteMoney ? "ON" : "OFF"}`, 0, () => {
        this.state.debugSetInfiniteMoney(!this.state.debugInfiniteMoney);
        this.rebuildDebugPanel();
      });
      addAction(`Infinite Health: ${this.state.debugInfiniteHealth ? "ON" : "OFF"}`, 1, () => {
        this.state.debugSetInfiniteHealth(!this.state.debugInfiniteHealth);
        this.rebuildDebugPanel();
      });
      addAction("Grant 100,000 Gold", 2, () => {
        this.state.debugAwardGold();
        status.setText("Gold granted.");
      });
      addAction("Heal Castle", 3, () => {
        this.state.debugHealCastle();
        status.setText("Castle health restored.");
      });
      addAction("Clear Active Enemies", 4, () => {
        this.state.debugClearEnemies();
        status.setText("Active enemies cleared.");
      });
      addAction("Send Next Wave", 5, () => {
        this.state.startWave();
        status.setText("Next wave requested.");
      });
      addAction("Force Victory", 6, () => this.state.debugWinBattle());
      addAction("Force Defeat", 7, () => this.state.debugLoseBattle());
    } else if (this.debugPage === 1) {
      addAction(`Enemy Movement: ${this.state.debugEnemyMovement ? "ON" : "PAUSED"}`, 0, () => {
        this.state.debugSetEnemyMovement(!this.state.debugEnemyMovement);
        this.rebuildDebugPanel();
      });
      addAction(`Tower Attacks: ${this.state.debugTowerAttacks ? "ON" : "PAUSED"}`, 1, () => {
        this.state.debugSetTowerAttacks(!this.state.debugTowerAttacks);
        this.rebuildDebugPanel();
      });
      [0.5, 1, 2, 4, 8].forEach((speed, index) => {
        addAction(`Set Game Speed ${speed}×`, index + 2, () => {
          this.state.debugSetSpeed(speed);
          status.setText(`Game speed set to ${speed}×.`);
        });
      });
      addAction("Spawn 10 Footmen", 7, () => {
        const count = this.state.debugSpawnEnemy("footman", 10);
        status.setText(`Spawned ${count} footmen.`);
      });
      addAction("Spawn Crown Champion", 8, () => {
        const count = this.state.debugSpawnEnemy("crown-champion", 1);
        status.setText(`Spawned ${count} crown champion.`);
      });
    } else {
      const enemyIds = Object.keys(ENEMIES);
      addAction("Spawn Random Enemy", 0, () => {
        const id = enemyIds[Math.floor(Math.random() * enemyIds.length)] ?? "footman";
        this.state.debugSpawnEnemy(id, 1);
        status.setText(`Spawned ${ENEMIES[id]?.displayName ?? id}.`);
      });
      addAction("Spawn Every Enemy Type", 1, () => {
        let count = 0;
        for (const id of enemyIds) count += this.state.debugSpawnEnemy(id, 1);
        status.setText(`Spawned ${count} enemy types.`);
      });
      addAction("Copy Diagnostic Report to Console", 2, () => {
        const report = {
          battle: this.state.debugDiagnosticSnapshot(),
          progression: progressionDiagnostics(loadSave().progress),
          save: saveDiagnostics(),
          platform: getPlatformSyncDiagnostic(),
          qa: completeQaAudit(),
        };
        console.info("[Defense QA diagnostic]", report);
        status.setText("Diagnostic report written to the browser console.");
      });
      addAction("Download Diagnostic JSON", 3, () => {
        const report = JSON.stringify(
          {
            battle: this.state.debugDiagnosticSnapshot(),
            progression: progressionDiagnostics(loadSave().progress),
            save: saveDiagnostics(),
            platform: getPlatformSyncDiagnostic(),
            qa: completeQaAudit(),
          },
          null,
          2,
        );
        const blob = new Blob([report], { type: "application/json" });
        const url = URL.createObjectURL(blob);
        const anchor = document.createElement("a");
        anchor.href = url;
        anchor.download = `littlecastle-defense-diagnostic-${Date.now()}.json`;
        anchor.click();
        URL.revokeObjectURL(url);
        status.setText("Diagnostic report downloaded.");
      });
      addAction("Stress Spawn 150", 4, () => {
        this.state.stressSpawn(150);
        status.setText("Stress wave spawned.");
      });
      addAction("Clear Enemies and Heal", 5, () => {
        this.state.debugClearEnemies();
        this.state.debugHealCastle();
        status.setText("Battlefield reset without changing campaign progress.");
      });
      addAction("Progression Unlock Diagnostic", 6, () => {
        const report = progressionDiagnostics(loadSave().progress);
        console.info("[Defense progression diagnostic]", report);
        status.setText(
          `Completed ${report.completedScenarioIds.length}/24 • Unlocked ${report.unlockedScenarioIds.length}/24 • Towers ${report.unlockedTowerIds.length}/20 • First→Second ${report.firstScenarioUnlocksSecond ? "PASS" : "FAIL"}`,
        );
      });
    }

    const navY = middle + height / 2 - 34;
    const previous = addButton(this, center - 150, navY, 120, "Previous", () => {
      this.debugPage = (this.debugPage + 2) % 3;
      this.rebuildDebugPanel();
    });
    const close = addButton(this, center, navY, 120, "Close", () =>
      this.debugPanel.removeAll(true),
    );
    const next = addButton(this, center + 150, navY, 120, "Next", () => {
      this.debugPage = (this.debugPage + 1) % 3;
      this.rebuildDebugPanel();
    });
    controls.push(previous, close, next);
    this.debugPanel.add(controls);
  }

  private showWaveAnnouncement(wave: number): void {
    const current = this.state.waves.currentWave;
    const name = current?.name ?? `Wave ${wave}`;
    const normalized = name.toLowerCase();
    const boss = ["boss", "champion", "warlord", "mammoth", "siege"].some((term) =>
      normalized.includes(term),
    );
    this.audio.cue(boss ? "boss-arrival" : "wave-start");
    this.presentAnnouncement(
      boss ? "BOSS APPROACHING" : `WAVE ${wave}`,
      name,
      boss ? 0x7f1f26 : 0x2f5fa7,
      boss ? 1900 : 1300,
    );
    if (boss && !loadSave().settings.reducedShake) this.cameras.main.shake(280, 0.008);
  }

  private showWaveComplete(wave: number): void {
    this.audio.cue("wave-clear");
    this.presentAnnouncement("ROAD SECURED", `Wave ${wave} defeated`, 0x55704a, 900);
  }

  private presentAnnouncement(
    heading: string,
    subtitle: string,
    accent: number,
    holdMs: number,
  ): void {
    this.announcement.removeAll(true);
    const centerX = this.scale.width / 2;
    const y = Math.max(105, this.scale.height * 0.18);
    const width = Math.min(460, this.scale.width - 36);
    const shadow = this.add.rectangle(5, 7, width, 82, 0x000000, 0.42);
    const panel = this.add
      .rectangle(0, 0, width, 82, 0x211a16, 0.96)
      .setStrokeStyle(3, COLORS.gold);
    const ribbon = this.add.rectangle(0, -31, width - 18, 12, accent, 0.95);
    const titleText = this.add
      .text(0, -8, heading, {
        fontFamily: "Georgia, serif",
        fontSize: this.scale.width < 700 ? "20px" : "25px",
        fontStyle: "bold",
        color: "#fff3cf",
        stroke: "#120d09",
        strokeThickness: 3,
      })
      .setOrigin(0.5);
    const subtitleText = this.add
      .text(0, 21, subtitle, {
        fontFamily: "Trebuchet MS, sans-serif",
        fontSize: this.scale.width < 700 ? "11px" : "13px",
        color: "#e8cf91",
      })
      .setOrigin(0.5);
    this.announcement.add([shadow, panel, ribbon, titleText, subtitleText]);
    this.announcement
      .setPosition(centerX, y - 20)
      .setAlpha(0)
      .setScale(0.94)
      .setVisible(true);
    this.tweens.killTweensOf(this.announcement);
    this.tweens.add({
      targets: this.announcement,
      y,
      alpha: 1,
      scaleX: 1,
      scaleY: 1,
      duration: 220,
      ease: "Back.Out",
      onComplete: () => {
        this.time.delayedCall(holdMs, () => {
          this.tweens.add({
            targets: this.announcement,
            y: y - 18,
            alpha: 0,
            duration: 220,
            onComplete: () => this.announcement.setVisible(false),
          });
        });
      },
    });
  }

  private rebuildPausePanel(): void {
    this.pausePanel.removeAll(true);
    if (!this.state.paused || this.state.ended) return;
    const x = this.scale.width / 2,
      y = this.scale.height / 2,
      width = Math.min(390, this.scale.width - 24),
      shade = this.add.rectangle(x, y, width, 230, 0x1c1713, 0.96).setStrokeStyle(3, COLORS.gold),
      heading = this.add
        .text(x, y - 78, "DEFENSE PAUSED", {
          fontFamily: "Georgia, serif",
          fontSize: "27px",
          fontStyle: "bold",
          color: "#fff3cf",
        })
        .setOrigin(0.5),
      resume = addButton(
        this,
        x,
        y - 20,
        220,
        "Resume Defense",
        () => {
          this.state.setPaused(false);
          this.rebuild();
        },
        true,
      ),
      restart = addButton(this, x, y + 38, 220, "Restart Castle Approach", () =>
        events.emit("game:restart-requested", undefined),
      ),
      exit = addButton(this, x, y + 88, 220, "Return to Main Menu", () =>
        events.emit("game:exit-requested", undefined),
      );
    this.pausePanel.add([shade, heading, resume, restart, exit]);
  }
  private rebuildPanel(): void {
    this.panel.removeAll(true);
    const id = this.state.selectedTower;
    const tower = id ? this.state.towers.get(id) : null;
    const definition = tower ? TOWERS[tower.definitionId] : null;
    if (!tower || !definition) return;

    const stats = towerStatsForInstance(definition, tower);
    const nextLevel = tower.level + 1;
    const nextOptions = definition.upgrades.filter((upgrade) => upgrade.level === nextLevel);
    const branchOptions =
      tower.branch === undefined
        ? nextOptions.filter((upgrade) => upgrade.branch === "a" || upgrade.branch === "b")
        : [];
    const next = tower.branch
      ? nextOptions.find(
          (upgrade) => upgrade.branch === "shared" || upgrade.branch === tower.branch,
        )
      : nextOptions.find((upgrade) => upgrade.branch === "shared" || !upgrade.branch);
    const choosingBranch = branchOptions.length === 2;
    const mobile = this.scale.width < 700;
    const width = Math.min(mobile ? this.scale.width - 16 : 620, this.scale.width - 20);
    const height = choosingBranch ? (mobile ? 222 : 194) : mobile ? 160 : 142;
    const x = this.scale.width / 2;
    const buildBarTop = this.scale.height - (mobile ? 122 : 75);
    const y = Math.max(height / 2 + 8, buildBarTop - height / 2 - 12);

    const background = this.add
      .rectangle(x, y, width, height, 0x27211c, 0.98)
      .setStrokeStyle(2, COLORS.gold)
      .setInteractive();

    const sellValue = this.state.economy.sellRefund(tower.invested, definition.sellRefundRate);
    const branchLabel = tower.branch
      ? definition.upgrades.find(
          (upgrade) => upgrade.level === 4 && upgrade.branch === tower.branch,
        )?.branchName
      : null;
    const description = choosingBranch
      ? "Choose one permanent evolution path. This choice cannot be changed."
      : (next?.description ?? "Maximum rank reached");
    const text = this.add
      .text(
        x - width / 2 + 14,
        y - height / 2 + 12,
        `${definition.displayName}  Rank ${tower.level}${branchLabel ? `  •  ${branchLabel}` : ""}
${Math.round(stats.attack.damage)} dmg  •  ${stats.range.toFixed(1)} range  •  ${(1000 / stats.attack.intervalMs).toFixed(2)}/s
${description}`,
        {
          fontFamily: "Trebuchet MS, sans-serif",
          fontSize: mobile ? "12px" : "13px",
          color: "#fff3cf",
          lineSpacing: 3,
          wordWrap: { width: width - 28 },
        },
      )
      .setOrigin(0, 0);

    const buttonGap = 8;
    const left = x - width / 2 + 14;
    const innerWidth = width - 28;
    const controls: Phaser.GameObjects.GameObject[] = [background, text];

    if (choosingBranch) {
      const branchY = y + height / 2 - (mobile ? 78 : 70);
      const branchWidth = (innerWidth - buttonGap) / 2;
      for (const [index, option] of branchOptions.entries()) {
        const branch = option.branch === "a" ? "a" : "b";
        controls.push(
          addButton(
            this,
            left + branchWidth / 2 + index * (branchWidth + buttonGap),
            branchY,
            branchWidth,
            `${option.name}  ◆${option.cost}`,
            () => this.state.upgradeTower(tower.id, branch),
            this.state.economy.canAfford(option.cost),
          ),
        );
      }
    }

    const buttonY = y + height / 2 - 29;
    const sellWidth = mobile ? 94 : 112;
    const actionWidth = (innerWidth - sellWidth - buttonGap * 2) / 2;
    const upgrade = addButton(
      this,
      left + actionWidth / 2,
      buttonY,
      actionWidth,
      choosingBranch ? "Choose Path Above" : next ? `Upgrade ◆${next.cost}` : "Maximum Rank",
      () => {
        if (next) this.state.upgradeTower(tower.id);
      },
      Boolean(next) && !choosingBranch && this.state.economy.canAfford(next?.cost ?? 0),
    );
    const mode = addButton(
      this,
      left + actionWidth + buttonGap + actionWidth / 2,
      buttonY,
      actionWidth,
      `Target: ${tower.targetingMode}`,
      () => this.state.cycleTargeting(tower.id),
    );

    let sellArmed = false;
    const sell = addButton(
      this,
      x + width / 2 - 14 - sellWidth / 2,
      buttonY,
      sellWidth,
      `Sell ◆${sellValue}`,
      () => {
        if (sellArmed) {
          this.state.sellTower(tower.id);
          return;
        }
        sellArmed = true;
        const label = sell.getAt(1);
        if (label instanceof Phaser.GameObjects.Text) label.setText("Confirm Sell");
        this.time.delayedCall(1600, () => {
          sellArmed = false;
          if (label instanceof Phaser.GameObjects.Text && label.active) {
            label.setText(`Sell ◆${sellValue}`);
          }
        });
      },
    );

    controls.push(upgrade, mode, sell);
    this.panel.add(controls);
  }

  private rebuildDraftPanel(): void {
    this.draftPanel.removeAll(true);
    const choices = this.state.pendingDraftChoices;
    if (choices.length === 0) return;

    const mobile = this.scale.width < 700;
    const width = Math.min(this.scale.width - 24, 900);
    const cardGap = 14;
    const cardWidth = (width - 40 - cardGap * (choices.length - 1)) / choices.length;
    const panelHeight = mobile ? 390 : 330;
    const x = this.scale.width / 2;
    const y = this.scale.height / 2;
    const shade = this.add
      .rectangle(
        this.scale.width / 2,
        this.scale.height / 2,
        this.scale.width,
        this.scale.height,
        0x080604,
        0.84,
      )
      .setInteractive();
    const background = this.add
      .rectangle(x, y, width, panelHeight, 0x211b17, 0.99)
      .setStrokeStyle(3, COLORS.gold);
    const heading = this.add
      .text(x, y - panelHeight / 2 + 34, "CHOOSE A ROYAL BOON", {
        fontFamily: "Georgia, serif",
        fontSize: mobile ? "22px" : "28px",
        fontStyle: "bold",
        color: "#fff3cf",
      })
      .setOrigin(0.5);
    const subtitle = this.add
      .text(
        x,
        y - panelHeight / 2 + 70,
        "Only towers in your current loadout are offered. Effects stack.",
        {
          fontFamily: "Trebuchet MS, sans-serif",
          fontSize: mobile ? "11px" : "13px",
          color: "#d8c38e",
        },
      )
      .setOrigin(0.5);
    const items: Phaser.GameObjects.GameObject[] = [shade, background, heading, subtitle];

    choices.forEach((choice, index) => {
      const tower = TOWERS[choice.towerId];
      if (!tower) return;
      const left = x - width / 2 + 20 + index * (cardWidth + cardGap);
      const cardX = left + cardWidth / 2;
      const cardY = y + 30;
      const currentStacks = this.state.towerAbilityStacks.get(choice.towerId) ?? 0;
      const card = this.add
        .rectangle(cardX, cardY, cardWidth, mobile ? 225 : 200, 0x332a22, 1)
        .setStrokeStyle(2, 0x9e7c3f)
        .setInteractive({ useHandCursor: true });
      const icon = this.add.image(cardX, cardY - 64, tower.texture).setDisplaySize(54, 54);
      const label = this.add
        .text(cardX, cardY - 24, `${tower.displayName}\n${choice.name}`, {
          fontFamily: "Georgia, serif",
          fontSize: mobile ? "13px" : "15px",
          fontStyle: "bold",
          color: "#fff3cf",
          align: "center",
          wordWrap: { width: cardWidth - 18 },
        })
        .setOrigin(0.5);
      const detail = this.add
        .text(cardX, cardY + 37, `${choice.description}\nCurrent stacks: ${currentStacks}`, {
          fontFamily: "Trebuchet MS, sans-serif",
          fontSize: mobile ? "11px" : "12px",
          color: "#c9d7cb",
          align: "center",
          wordWrap: { width: cardWidth - 20 },
        })
        .setOrigin(0.5);
      const choose = addButton(
        this,
        cardX,
        cardY + (mobile ? 91 : 80),
        Math.max(90, cardWidth - 24),
        "Choose",
        () => this.state.chooseDraftAbility(choice.id),
        true,
      );
      card.on("pointerdown", () => this.state.chooseDraftAbility(choice.id));
      items.push(card, icon, label, detail, choose);
    });
    this.draftPanel.add(items);
  }

  private flash(message: string): void {
    this.message.setText(message).setVisible(true);
    this.tweens.killTweensOf(this.message);
    this.message.setAlpha(1);
    this.tweens.add({
      targets: this.message,
      alpha: 0,
      delay: 1600,
      duration: 300,
      onComplete: () => this.message.setVisible(false),
    });
  }
}
